import { useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { debounce } from '@dfinity/utils'
import useBalances from '../useBalances'
import useSwap, { SwapPairPrice } from '../useSwap'
import { formatNumber } from '../../utils/formatNumber'
import { UseStakeButtonProps } from '../../interfaces/staking'
import { STAKE_COMMISSION } from '../../constants/business'

export const useUnstakeButton = ({
  nicpToken,
  icpToken,
  waterNeuron,
  wnInfo,
  amount,
  commission,
  review,
}: UseStakeButtonProps) => {
  const { findBalance } = useBalances()
  const { t } = useTranslation()

  const { decimals, fee } = nicpToken

  const { performSwap, getQuotes } = useSwap({
    fromTokenId: nicpToken.id,
    toTokenId: icpToken.id,
    fromValue: ((amount + commission) * 10 ** -decimals).toString(),
  })

  const [unstakeMode, setUnstakeMode] = useState<'immediate' | 'delayed'>('delayed')
  const [quote, setQuote] = useState<SwapPairPrice>()
  const [prompt, setPrompt] = useState<{
    title: string
    description: string
  } | null>(null)

  const debouncedGetQuotes = useMemo(
    () =>
      debounce(async (amount: number, commission: number) => {
        if (amount) {
          const normalizedAmount = (amount + commission) * 10 ** -decimals
          const quotes = await getQuotes(normalizedAmount.toString())
          const bestQuote = quotes.sort((a, b) => b.price - a.price)[0]

          setQuote(bestQuote)
        } else {
          setQuote(undefined)
        }
      }, 500),
    [getQuotes, decimals]
  )

  useEffect(() => debouncedGetQuotes(amount, commission), [amount, commission])

  const balance = findBalance(nicpToken)?.balance || 0
  const exchangeRate = Number(wnInfo.exchange_rate)
  const minWithdraw = Number(wnInfo.minimum_withdraw_amount) * exchangeRate * 10 ** -decimals

  let userError
  if (!amount) {
    userError = t('stake.noAmount')
  } else if (amount + commission + fee! * 2 > balance * 10 ** decimals) {
    userError = t('stake.notEnough', { tokenSymbol: 'nICP' })
  } else if (!unstakeMode) {
    userError = t('stake.chooseUnstakeMode')
  } else if (unstakeMode === 'delayed' && amount < minWithdraw) {
    userError = t('stake.withdrawTooSmall', {
      minWithdraw: formatNumber(
        (minWithdraw / (1 - STAKE_COMMISSION)) * 10 ** -icpToken.decimals,
        '',
        { maximumFractionDigits: 6 }
      ),
    })
  } else if (unstakeMode === 'immediate' && !quote) {
    userError = t('stake.noSwapQuotesAvailable')
  }

  const swapNicp = async () => {
    if (quote) {
      await performSwap(quote.pair, quote.price)
    } else {
      setPrompt({
        title: t('stake.unstake.swapUnavailable'),
        description: t('stake.unstake.noSwapOffers'),
      })
    }
  }

  const unstakeNicp = async () => {
    await waterNeuron.unstake(Math.floor(amount), nicpToken)
  }

  const reviewStake = () => {
    let toAmount
    if (unstakeMode === 'delayed') {
      toAmount = amount / exchangeRate
    } else {
      toAmount = quote?.humanPrice || 0
    }

    const attrs = {
      [t('stake.review.unstakeMode')]: t(`stake.unstake.${unstakeMode}.title`),
    }

    if (unstakeMode === 'immediate') {
      attrs[t('stake.review.swapProvider')] = quote?.pair.provider || ''
    }

    review({
      toAmount: formatNumber(toAmount)!,
      attrs,
      action: async () => {
        if (unstakeMode === 'immediate') {
          await swapNicp()
        } else {
          await unstakeNicp()
        }
      },
    })
  }

  const onInfoClick = (event: any, stakeMode: 'immediate' | 'delayed') => {
    setPrompt({
      title: t(`stake.unstake.${stakeMode}.title`),
      description: t(`stake.unstake.${stakeMode}.description`),
    })
  }

  return {
    setUnstakeMode,
    unstakeMode,
    onInfoClick,
    userError,
    reviewStake,
    setPrompt,
    prompt,
    delayedAmount: amount ? formatNumber(amount / exchangeRate, 'ICP') : '-/-',
    immediateAmount: quote ? formatNumber(quote.humanPrice, 'ICP') : '-/-',
  }
}
